import { Link } from "@tanstack/react-router";
import { Bot, Cog, Shield, User } from "lucide-react";
import type { AuditEvent } from "@/api/types";
import { AiRunLink, ConversationLink, IntegrationLink, OpportunityLink, OrgLink, StoreLink, UserLink } from "@/components/navigation/entity-links";

const ACTOR_ICON = { ADMIN: Shield, USER: User, ROGUE: Bot, SYSTEM: Cog } as const;

export function ActorLabel({ actor }: { actor: AuditEvent["actor"] }) {
  const Icon = ACTOR_ICON[actor.type] ?? User;
  const label = actor.name ?? actor.type.toLowerCase();
  return (
    <span className="inline-flex items-center gap-1.5" title={actor.type}>
      <Icon className="size-3.5 text-muted-foreground" aria-hidden />
      {actor.id && (actor.type === "ADMIN" || actor.type === "USER") ? <UserLink user={{ id: actor.id, name: label }} /> : <span>{label}</span>}
    </span>
  );
}

/** Links an audit resource to its page when the console has one. */
export function ResourceRefLink({ resource }: { resource: AuditEvent["resource"] }) {
  const { type, id, label } = resource;
  const text = `${type.replaceAll("_", " ")}${label ? ` · ${label}` : ""}`;
  if (!id) return <span>{text}</span>;
  switch (type) {
    case "organization":
      return <OrgLink org={{ id, name: text }} />;
    case "store":
      return <StoreLink store={{ id, name: text }} />;
    case "user":
      return <UserLink user={{ id, name: text }} />;
    case "opportunity":
      return <OpportunityLink id={id} label={text} />;
    case "conversation":
      return <ConversationLink id={id} label={text} />;
    case "ai_run":
      return <AiRunLink id={id} label={text} />;
    case "integration":
      return <IntegrationLink id={id} name={text} />;
    case "admin":
      return (
        <Link to="/team/$staffId" params={{ staffId: id }} className="hover:underline">
          {`staff · ${label ?? id}`}
        </Link>
      );
    default:
      return <span>{text}</span>;
  }
}
